import type { DsdFileRaw, GetTaskStatusResponse } from "@/types/dsd";
import request from "../axios-instance";

import { getLlmCacheEnabled } from "@/lib/llm-cache";
import { getLlmMode } from "@/lib/llm-mode";
import {
  AnalyzeDsdResponse,
  VersionForwardCheckResponse,
  VersionForwardResponse,
  VersionForwardReviewResponse,
} from "./types";

export const getTaskStatus = async (
  workbookId: string,
): Promise<GetTaskStatusResponse> => {
  const response = await request.get(`/v1/dsd/${workbookId}/task-status`);
  return response.data;
};

export const getDsdFiles = async (): Promise<DsdFileRaw[]> => {
  const response = await request.get("/v1/dsd/list");

  return response.data.workbooks as DsdFileRaw[];
};

export const deleteDsdFile = async (fileId: string) => {
  const response = await request.delete(`/v1/workbooks/${fileId}`);
  return response.data;
};

// DSD 비교대사를 위한 분석 API
export const analyzeDsd = async (
  file: File,
  options?: { autoAnalyzeXbrl?: boolean },
): Promise<AnalyzeDsdResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  if (options?.autoAnalyzeXbrl) {
    formData.append("auto_analyze_xbrl", "true");
  }
  const liteMode = getLlmMode();
  if (liteMode !== "none") {
    formData.append("liteMode", liteMode);
  }
  if (!getLlmCacheEnabled()) {
    formData.append("useCache", "false");
  }

  const response = await request.post("/v1/dsd/analyze-dsd", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

/**
 * 이 파일의 버전 포워드 후보(같은 파일의 직전 분석완료본)를 영속 없이 조회한다.
 * candidates 가 있으면 "최신 버전으로 업로드" 안내에 사용.
 */
export const checkVersionForward = async (
  file: File,
): Promise<VersionForwardCheckResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await request.post("/v1/dsd/version-forward/check", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

/**
 * DSD 업로드 + 버전 포워드 + XBRL 분석. srcWorkbookId 가 오면 그 직전본의
 * 태깅을 이어받아 새 리비전으로 올린다("최신 버전으로 업로드").
 */
export const analyzeDsdWithVersionForward = async (
  file: File,
  options?: { srcWorkbookId?: string | null },
): Promise<VersionForwardResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  if (options?.srcWorkbookId) {
    formData.append("srcWorkbookId", options.srcWorkbookId);
  }
  const liteMode = getLlmMode();
  if (liteMode !== "none") {
    formData.append("liteMode", liteMode);
  }

  const response = await request.post("/v1/dsd/version-forward", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

/**
 * 버전 포워드 표별 변경 분류 조회. 직전본(src)·새 파일(trg) 워크북을 표(태깅 단위)로
 * 대사해 trg 표별 변경 종류를 돌려준다 — "변경 사항 확인" 모달 원천.
 */
export const getVersionForwardReview = async (
  srcWorkbookId: string,
  trgWorkbookId: string,
): Promise<VersionForwardReviewResponse> => {
  const response = await request.get("/v1/dsd/version-forward-review", {
    params: { srcWorkbookId, trgWorkbookId },
  });
  return response.data;
};

export const convertDsdOrExcel = async (file: File): Promise<Blob> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await request.post("/v1/dsd/convert/file", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });

  return response.data;
};

export type DartConvertFormat = "xlsx" | "dsd";

export const convertDart = async (
  url: string,
  toFormat: DartConvertFormat,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await request.post(
    "/v1/dsd/convert/dart",
    { url, to_format: toFormat },
    {
      responseType: "blob",
    },
  );

  const disposition = response.headers["content-disposition"] ?? "";
  const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : `dart_convert.${toFormat}`;

  return { blob: response.data, filename };
};

export type AccountingSheetReportType = "audit" | "pre_audit";

export const convertAccountingSheet = async (
  file: File,
  params: {
    reportType: AccountingSheetReportType;
    scope: string;
    /** report_type="audit"일 때만 필수. pre_audit은 기간 개념 없음 */
    period?: "quarterly_1" | "semi_annual" | "quarterly_3" | "annual";
    /** 회계연도. 표지 당기 기간(1/1 ~ 기간 말일) 자동 입력에 사용 */
    year: number;
  },
): Promise<{ blob: Blob; filename: string }> => {
  const { reportType, scope, period, year } = params;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("scope", scope);
  formData.append("report_type", reportType);
  if (period) {
    formData.append("period", period);
  }
  formData.append("year", String(year));

  const response = await request.post(
    "/v1/dsd/convert/accounting-sheet",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      responseType: "blob",
    },
  );

  const disposition = response.headers["content-disposition"] ?? "";
  const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : "accounting_sheet.dsd";

  return { blob: response.data, filename };
};

export const analyzeXbrl = async (workbookId: string): Promise<void> => {
  const params: Record<string, string> = {};
  const liteMode = getLlmMode();
  if (liteMode !== "none") params.liteMode = liteMode;
  if (!getLlmCacheEnabled()) params.useCache = "false";
  await request.post(`/v1/dsd/${workbookId}/analyze-xbrl`, undefined, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
};

export const analyzeXbrlBySheets = async (
  workbookId: string,
  sheetIds: string[],
): Promise<void> => {
  const liteMode = getLlmMode();
  await request.post(`/v1/dsd/${workbookId}/analyze-xbrl-by-sheets`, {
    sheetIds: sheetIds,
    ...(liteMode === "none" ? {} : { liteMode }),
    ...(getLlmCacheEnabled() ? {} : { useCache: false }),
  });
};
